import type { ReactNode } from 'react';
import { STATUS_GLYPH_PATHS, STATUS_GLYPH_RING, STATUS_GLYPH_VIEWBOX } from '@d-orca/shared/client/activities';

// ---------------------------------------------------------------------------
// The SHAPES of the per-area status marks, drawn in one place. Two components
// render Trusstor's area vocabulary with opposite colour polarity — the
// Scheduler board's `StatusMark` (a saturated mark on a pale ground) and the
// Progress Tracker's `StatusGlyph` (a light mark on a vivid fill) — so each
// keeps its own stroke weights. What they must not do is disagree on the
// geometry: typed out twice, the check and the ring had already drifted apart.
//
// Every shape sits on the same 14×14 viewBox and paints with `currentColor`,
// so it inherits the host cell's foreground. The path data is shared with the
// mobile app (`STATUS_GLYPH_PATHS`); the renderers here are the web's.
// ---------------------------------------------------------------------------

interface IGlyphSvgProps {
  size: number;
  /** Tags the shape for tests and styling (e.g. `flag`). */
  mark?: string;
  children: ReactNode;
}

/** The 14×14 frame every status mark draws in — decorative, never focusable. */
export function GlyphSvg({ size, mark, children }: IGlyphSvgProps) {
  return (
    <svg width={size} height={size} viewBox={STATUS_GLYPH_VIEWBOX} aria-hidden focusable="false" data-mark={mark}>
      {children}
    </svg>
  );
}

interface IGlyphStrokeProps {
  strokeWidth: number;
}

/** Done — a check. */
export function GlyphCheck({ strokeWidth }: IGlyphStrokeProps) {
  return (
    <path
      d={STATUS_GLYPH_PATHS.check}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

/** Nothing reported yet — a hollow ring. */
export function GlyphRing({ strokeWidth }: IGlyphStrokeProps) {
  return <circle {...STATUS_GLYPH_RING} fill="none" stroke="currentColor" strokeWidth={strokeWidth} />;
}

/** Under way — the same ring with its right half filled. */
export function GlyphHalfDisc({ strokeWidth }: IGlyphStrokeProps) {
  return (
    <>
      <GlyphRing strokeWidth={strokeWidth} />
      <path d={STATUS_GLYPH_PATHS.halfFill} fill="currentColor" />
    </>
  );
}

/** Past its date — a pennant on a pole. */
export function GlyphFlag({ strokeWidth }: IGlyphStrokeProps) {
  return (
    <>
      <path d={STATUS_GLYPH_PATHS.flagPole} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d={STATUS_GLYPH_PATHS.flag} fill="currentColor" />
    </>
  );
}
